import { Shirt } from "../../entities/Shirt";
import { TopUser, UserData } from "../../types/authTypes";

export interface Condition {
  id: number;
  name: string;
}

export interface Brand {
  id: number;
  name: string;
  logo: string;
}

export interface Competition {
  id: string;
  name: string;
  logo: string;
  country: string;
  sport: string;
}

export interface ShirtType {
  id: number;
  name: string;
  description: string;
}

export interface Player {
  id: string;
  alias: string | undefined;
  name: string;
  lastname: string;
  birthday: string;
  country: string;
  picture?: string;
}

export interface PlayerWithCount extends Player {
  appearances: number;
}

export interface Season {
  id: string;
  start_year: string;
  end_year: string;
}

export interface Team {
  id: string;
  name: string;
  city: string;
  country: string;
  logo: string;
  item_count?: number;
  national_team?: boolean;
}

export interface TeamWithCount extends Team {
  appearances: number;
}

export interface TopResponse {
  data: Array<PlayerWithCount | TeamWithCount | TopUser>;
  total: number;
  lastPage: number;
}

export interface Variant {
  id: number;
  name: string;
  description: string;
}

export interface Signature {
  id?: number;
  description: string;
  item_id?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ShirtModelInterface {
  id: number;
  type: ShirtType;
  number: number;
  text: string;
  brand: Brand;
  competition: Competition;
  player: Player;
  season: Season;
  team: Team;
  variant: Variant;
}

export interface SearchShirtResponse {
  lastPage: number | undefined;
  shirts: Array<Shirt>;
  total?: number;
}

export interface ShirtOwnerInfoInterface {
  id: number;
  avatar: string;
  username: string;
  is_vip?: boolean;
  country?: string;
}

export interface ShirtInterface {
  id: number;
  title: string;
  description: string;
  purchase_date: string;
  purchase_price: string;
  certificate: boolean;
  is_private: boolean;
  condition: Condition;
  model: ShirtModelInterface;
  signature: Signature;
  properties: any;
  mainImage: string | undefined;
  owner: ShirtOwnerInfoInterface;
  images: Array<any>;
  irresistible_offers: boolean;
  current_price?: number;
  size?: string;
  legitCheckResults?: {
    total: number;
    positive: number;
  };
  // Custom fields for when shirt was created with custom values
  team_custom?: string | undefined;
  brand_custom?: string | undefined;
  player_custom?: string | undefined;
  custom_number?: number | undefined;
}

export interface PublicUserData {
  id: number;
  username: string;
  userData: UserData;
  profile_image: Image | undefined;
}
export interface PublicShirt {
  id: number;
  title: string;
  description: string;
  purchase_date: string;
  purchase_price: string;
  certificate: boolean;
  is_private: boolean;
  condition: Condition;
  model: ShirtModelInterface;
  signature: Signature;
  properties: any;
  mainImage: string | undefined;
  owner: PublicUserData;
  images: Array<any>;
  current_price?: number;
}

export interface SearchShopShirtResponse {
  lastPage: number | undefined;
  shirts: Array<ShopShirtInterface>;
  total: number;
}

export interface ExternalShop {
  id: number;
  name: string;
  url: string | undefined;
  logo: string | undefined;
}
export interface ShopShirtInterface {
  id: number;
  original_name: string;
  original_description: string;
  name: string | undefined;
  description: string | undefined;
  price: number;
  url: string;
  images: Array<any>;
  sold: boolean;
  public: boolean;
  model?: ShirtModelInterface;
  shop: ExternalShop;
}

export interface MatchData {
  date: string | undefined;
  home: string | undefined;
  away: string | undefined;
  resultH: number | undefined;
  resultA: number | undefined;
}

export interface ExtraDataMatch {
  match: MatchData;
}

export interface Property {
  id: number;
  key: string;
  extra_data?: any;
}

export interface NewShirt {
  title: string | undefined;
  description: string | undefined;
  purchase_date: string | undefined; //2020/12/12,
  purchase_price: number | undefined;
  certificate: boolean | undefined;
  private: boolean | undefined;
  condition_id: number | undefined;
  signature: boolean | undefined;
  signature_data: string | undefined;
  player_id?: string | undefined;
  team_id: string | undefined;
  competition_id: string | undefined;
  variant_id: number | undefined;
  season_id: string | undefined;
  number?: number | undefined;
  text?: string | undefined;
  custom_number?: number;
  brand_id: number | undefined;
  shirt_type_id: number | undefined;
  irresistible_offers?: boolean;
  size?: string;
  // Custom fields for when user enters custom values
  team_custom?: string | undefined;
  brand_custom?: string | undefined;
  player_custom?: string | undefined;
}

export const shirtDefault: NewShirt = {
  title: undefined,
  description: undefined,
  purchase_date: undefined, //2020/12/12,
  purchase_price: undefined,
  certificate: false,
  private: false,
  condition_id: undefined,
  signature: false,
  signature_data: undefined,
  player_id: undefined,
  team_id: undefined,
  competition_id: undefined,
  variant_id: undefined,
  season_id: undefined,
  number: undefined,
  text: undefined,
  brand_id: undefined,
  shirt_type_id: undefined,
  irresistible_offers: true,
};

export interface Image {
  id?: number;
  name: string;
  data?: string;
  url?: string;
}

export interface TopItemInterface {
  position: number;
  legit_check_count: number;
  legit_check_count_true: number;
  item: ShirtInterface;
}

export interface TopItem {
  position: number;
  item: Shirt;
}

export interface FinancialElement {
  year: number;
  month: number;
  total_price: string;
  total_purchase_price: string;
  total_profit: string;
}

export interface FinancialItemElement {
  year: number;
  month: number;
  price: string;
  purchase_price: string;
  profit: string;
}

export interface CollectionSizeElement {
  year: string;
  count: number;
}

export interface ShirtsNameCountElement {
  name: string;
  count: number;
}

export interface ShirtsCountryCountElement {
  country: string;
  count: number;
}

export interface ShirtsSeasonCountElement {
  id: number;
  count: number;
}

export interface ShirtsDecadeCountElement {
  decade: string;
  count: number;
}

export interface Collector {
  id: number;
  user_name: string;
  item_count: number;
  profile_image: Image;
  biggest_club_collection_name: string;
  biggest_club_collection_count: number;
  match_worn_item_count: number;
  collection_value: number;
  followers?: number;
  following?: number;
  is_vip: boolean;
  country: string;
}
